import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatError } from "@/lib/api";
import { AiLabFiles } from "./AiLabFiles";
import {
  Sparkles, Upload, Wand2, Image as ImageIcon, Film, Loader2, AlertTriangle,
  RefreshCw, Play, Users, Trash2, Plus, Check,
} from "lucide-react";

const TASKS = [
  { key: "tattoo_removal", label: "Tattoo entfernen", icon: Wand2,
    hint: "Gleiche Person, gleiche Pose – nur ohne Tattoos (fester Prompt)." },
  { key: "image_edit", label: "Bild bearbeiten (Prompt)", icon: ImageIcon,
    hint: "Frei prompten – optional mit Avatar-Referenz." },
  { key: "image_to_video", label: "Bild → Video (Prompt)", icon: Film,
    hint: "Aus dem Bild einen kurzen Clip erzeugen (Wan 2.2)." },
];
const TERMINAL = ["COMPLETED", "FAILED", "CANCELLED"];

export default function AiLabSection() {
  const [cfg, setCfg] = useState(null);
  const [task, setTask] = useState("tattoo_removal");
  const [view, setView] = useState("lab");  // "lab" (Generieren) | "files" (Archiv)
  const [lane, setLane] = useState("a");  // "a"=Kandidat 2511/24GB MIG, "b"=Vergleich 2509/96GB
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [cfgScale, setCfgScale] = useState(4.0);
  const [seed, setSeed] = useState(0);
  const [steps, setSteps] = useState(40);
  const [enhancing, setEnhancing] = useState(false);
  const [src, setSrc] = useState(null);
  const [src2, setSrc2] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploading2, setUploading2] = useState(false);
  const [job, setJob] = useState(null);
  const [resultUrl, setResultUrl] = useState(null);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [recent, setRecent] = useState([]);
  // Avatar
  const [avatars, setAvatars] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [newName, setNewName] = useState("");
  const [newFiles, setNewFiles] = useState([]);
  const [creatingAvatar, setCreatingAvatar] = useState(false);
  const [health, setHealth] = useState(null);
  const [waitSec, setWaitSec] = useState(0);
  const pollRef = useRef(null);
  const waitRef = useRef(null);

  const loadConfig = useCallback(async () => {
    try { const { data } = await api.get("/admin/ai-lab/config"); setCfg(data); }
    catch (e) { setErr(formatError(e)); }
  }, []);
  const loadRecent = useCallback(async () => {
    try { const { data } = await api.get("/admin/ai-lab/jobs?limit=12"); setRecent(data.jobs || []); }
    catch { /* ignore */ }
  }, []);
  const loadAvatars = useCallback(async () => {
    try { const { data } = await api.get("/admin/ai-lab/avatars"); setAvatars(data.avatars || []); }
    catch { /* ignore */ }
  }, []);

  const loadHealth = useCallback(async (ln) => {
    try { const { data } = await api.get(`/admin/ai-lab/health?lane=${ln || lane}`); setHealth(data); }
    catch { setHealth(null); }
  }, [lane]);

  useEffect(() => { loadConfig(); loadRecent(); loadAvatars(); return () => clearTimeout(pollRef.current); },
    [loadConfig, loadRecent, loadAvatars]);

  // Live-Health des RunPod-Endpoints: alle 10 s, solange Tab offen & aktiviert.
  useEffect(() => {
    if (!cfg?.enabled) return;
    loadHealth();
    const id = setInterval(loadHealth, 10000);
    return () => clearInterval(id);
  }, [cfg, loadHealth]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(""); setResultUrl(null); setJob(null); setReport(null); setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/ai-lab/upload", fd);
      setSrc({ source_path: data.source_path, previewUrl: URL.createObjectURL(f) });
    } catch (e2) { setErr(formatError(e2)); }
    finally { setUploading(false); }
  };

  const onFile2 = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(""); setUploading2(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/ai-lab/upload", fd);
      setSrc2({ source_path: data.source_path, previewUrl: URL.createObjectURL(f) });
    } catch (e2) { setErr(formatError(e2)); }
    finally { setUploading2(false); }
  };

  const enhance = async () => {
    if (!prompt.trim()) { setErr("Bitte zuerst eine kurze Idee ins Prompt-Feld schreiben."); return; }
    setErr(""); setEnhancing(true);
    try {
      const av = avatars.find((a) => a.avatar_id === selectedAvatar);
      const { data } = await api.post("/admin/ai-lab/enhance-prompt",
        { task, idea: prompt.trim(), avatar_name: av?.name || null });
      if (data.positive || data.prompt) setPrompt(data.positive || data.prompt);
      if (typeof data.negative === "string" && data.negative) setNegativePrompt(data.negative);
    } catch (e) { setErr(formatError(e)); }
    finally { setEnhancing(false); }
  };

  const createAvatar = async () => {
    if (!newName.trim()) { setErr("Avatar-Name fehlt."); return; }
    if (!newFiles.length) { setErr("Bitte mehrere Bilder der Person wählen."); return; }
    setErr(""); setCreatingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("name", newName.trim());
      newFiles.forEach((f) => fd.append("files", f));
      await api.post("/admin/ai-lab/avatars", fd);
      setNewName(""); setNewFiles([]);
      loadAvatars();
    } catch (e) { setErr(formatError(e)); }
    finally { setCreatingAvatar(false); }
  };

  const deleteAvatar = async (id) => {
    try { await api.delete(`/admin/ai-lab/avatars/${id}`);
      if (selectedAvatar === id) setSelectedAvatar(null);
      loadAvatars();
    } catch (e) { setErr(formatError(e)); }
  };

  const cancelJob = async () => {
    if (!job?.job_id) return;
    clearTimeout(pollRef.current);
    try {
      await api.post(`/admin/ai-lab/jobs/${job.job_id}/cancel`);
      setJob((j) => (j ? { ...j, status: "CANCELLED" } : j));
      loadRecent();
    } catch (e) { setErr(formatError(e)); }
  };

  const fetchResult = useCallback(async (jobId) => {
    try {
      const { data } = await api.get(`/admin/ai-lab/jobs/${jobId}/output`, { responseType: "blob" });
      setResultUrl(URL.createObjectURL(data));
    } catch (e) { setErr(formatError(e)); }
  }, []);

  const fetchReport = useCallback(async (jobId) => {
    try { const { data } = await api.get(`/admin/ai-lab/jobs/${jobId}/report`); setReport(data); }
    catch { /* ignore */ }
  }, []);

  const poll = useCallback((jobId) => {
    clearTimeout(pollRef.current);
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/ai-lab/jobs/${jobId}`);
        setJob(data);
        if (TERMINAL.includes(data.status)) {
          loadRecent();
          fetchReport(jobId);
          if (data.status === "COMPLETED" && data.output_url) fetchResult(jobId);
          else if (data.status === "FAILED") setErr(data.error || "Verarbeitung fehlgeschlagen.");
          return;
        }
        poll(jobId);
      } catch (e) { setErr(formatError(e)); }
    }, 3000);
  }, [fetchResult, fetchReport, loadRecent]);

  const submit = async () => {
    if (task === "tattoo_removal" && !src?.source_path) { setErr("Für die Tattoo-Entfernung bitte ein Foto hochladen."); return; }
    if (!src?.source_path && !selectedAvatar) { setErr("Bitte ein Testbild hochladen oder einen Avatar wählen."); return; }
    if (task !== "tattoo_removal" && !prompt.trim()) { setErr("Bitte einen Prompt eingeben."); return; }
    if (task !== "image_to_video" && !cfg?.ai_lab_lanes?.[lane]?.configured) {
      setErr(lane === "b"
        ? "Der Vergleichs-Endpunkt B (2509) ist nicht konfiguriert. Bitte RUNPOD_AI_LAB_IMAGE_ENDPOINT_B (und optional RUNPOD_AI_LAB_API_KEY_B) als Test-/Vorschau-Wert setzen."
        : "Der KI-Labor-Kandidat A (2511) ist nicht konfiguriert. Bitte RUNPOD_AI_LAB_API_KEY und RUNPOD_AI_LAB_IMAGE_ENDPOINT als Test-/Vorschau-Wert setzen – es wird bewusst nicht auf den produktiven Endpoint zurückgegriffen.");
      return;
    }
    setErr(""); setResultUrl(null); setReport(null);
    try {
      const body = { task };
      if (src?.source_path) body.source_path = src.source_path;
      if (task === "image_edit" && src2?.source_path) body.source_path_2 = src2.source_path;
      if (selectedAvatar) body.avatar_id = selectedAvatar;
      if (task !== "tattoo_removal") body.prompt = prompt.trim();
      if (task !== "image_to_video" && negativePrompt.trim()) body.negative_prompt = negativePrompt.trim();
      if (task !== "image_to_video") { body.true_cfg_scale = cfgScale; body.steps = steps; body.endpoint_lane = lane; }
      body.seed = Number.isFinite(seed) ? seed : 0;
      const { data } = await api.post("/admin/ai-lab/jobs", body);
      setJob({ job_id: data.job_id, status: data.status, kind: data.kind });
      poll(data.job_id);
    } catch (e) { setErr(formatError(e)); }
  };

  const busy = job && !TERMINAL.includes(job.status);
  const isVideo = task === "image_to_video";
  const BASE = process.env.REACT_APP_BACKEND_URL;

  // Wartezeit-Zähler (sekundengenau), solange ein Job läuft; Health seltener aktualisieren.
  useEffect(() => {
    if (!busy) { clearInterval(waitRef.current); setWaitSec(0); return; }
    setWaitSec(0);
    const start = Date.now();
    let ticks = 0;
    waitRef.current = setInterval(() => {
      setWaitSec(Math.floor((Date.now() - start) / 1000));
      ticks += 1;
      if (cfg?.enabled && ticks % 4 === 0) loadHealth();  // Health alle ~4 s
    }, 1000);
    return () => clearInterval(waitRef.current);
  }, [busy, cfg, loadHealth]);

  const HEALTH_UI = {
    ready:     { cls: "border-emerald-300 bg-emerald-50 text-emerald-800", dot: "bg-emerald-500", label: "GPU bereit" },
    idle:      { cls: "border-[#DDD] bg-[#FAFAFA] text-[#666]", dot: "bg-[#AAA]", label: "Leerlauf (startet bei Bedarf)" },
    warming:   { cls: "border-amber-300 bg-amber-50 text-amber-800", dot: "bg-amber-500 animate-pulse", label: "GPU startet …" },
    throttled: { cls: "border-red-300 bg-red-50 text-red-700", dot: "bg-red-500 animate-pulse", label: "GPU gerade nicht verfügbar" },
  };
  const hState = health?.state && HEALTH_UI[health.state] ? HEALTH_UI[health.state] : null;
  // Warteschlangen-Hinweis: Job steckt in IN_QUEUE und entweder throttled oder >15 s.
  const queueStuck = busy && job?.status === "IN_QUEUE" &&
    (health?.state === "throttled" || waitSec >= 15);

  return (
    <div data-testid="ai-lab-section" className="max-w-5xl">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={18} className="text-[#0A0A0A]" />
        <h2 className="text-lg font-bold text-[#0A0A0A]">KI-Labor · Virtueller Avatar (Test)</h2>
      </div>
      <p className="text-xs text-[#666] mb-5 max-w-2xl">
        Eigene GPU-Bildgenerierung (RunPod, self-hosted, ohne externe Zensur-APIs). Phase 1: Tattoo-Entfernung.
        Zusätzlich frei prompten, KI-Prompt-Optimierung und einen Referenz-Avatar aus mehreren Personenbildern –
        um zu sehen, was für Modul G/H möglich ist.
      </p>

      <div className="flex items-center gap-1 mb-5 border-b border-[#EEE]">
        {[["lab", "Labor"], ...(cfg?.is_owner ? [["files", "Dateien"]] : [])].map(([k, lbl]) => (
          <button key={k} data-testid={`ai-lab-tab-${k}`} onClick={() => setView(k)}
            className={`px-3 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors ${view === k ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#555]"}`}>
            {lbl}
          </button>
        ))}
      </div>

      {view === "files" && cfg?.is_owner ? (
        <AiLabFiles BASE={BASE} />
      ) : (
      <>
      {/* Live-Health des RunPod-Endpoints */}
      {cfg?.enabled && (
        <div data-testid="ai-lab-health" className="flex flex-wrap items-center gap-2 mb-5">
          <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${hState ? hState.cls : "border-[#DDD] bg-[#FAFAFA] text-[#999]"}`}>
            <span className={`w-2 h-2 rounded-full ${hState ? hState.dot : "bg-[#CCC]"}`} />
            <span data-testid="ai-lab-health-label">{hState ? hState.label : "Status wird geladen …"}</span>
          </div>
          {health && (
            <span className="text-[11px] text-[#888]">
              Warteschlange: {health.jobs.in_queue} · aktiv: {health.jobs.in_progress} ·
              Worker bereit/laufend: {health.workers.ready + health.workers.idle}/{health.workers.running}
              {health.workers.throttled > 0 && <span className="text-red-600"> · throttled: {health.workers.throttled}</span>}
            </span>
          )}
          <button data-testid="ai-lab-health-refresh" onClick={loadHealth}
            className="text-[#AAA] hover:text-[#0A0A0A]" title="Aktualisieren"><RefreshCw size={13} /></button>
          {health?.state === "throttled" && (
            <p className="w-full text-[11px] text-red-600 mt-0.5">
              RunPod hat aktuell keine passende GPU frei. Tipp: im Endpoint mehrere GPU-Typen erlauben
              (z. B. RTX PRO 6000 + A100 + L40S) und ggf. Active-Worker kurz auf 1 setzen.
            </p>
          )}
        </div>
      )}

      {cfg && !cfg.enabled && (
        <div data-testid="ai-lab-disabled-banner"
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 mb-5 text-sm text-amber-900">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div><b>Noch nicht aktiviert.</b> Der RunPod-Worker ist noch nicht verbunden
            (RUNPOD_ENABLED=false{cfg.has_key ? "" : ", kein API-Key"}). Anleitung:
            <code className="mx-1 px-1 bg-white rounded">/app/ai_worker/README_DEPLOY.md</code>.
            Avatar-Anlegen und Prompt-Optimierung funktionieren aber bereits.</div>
        </div>
      )}

      {cfg && cfg.enabled && !cfg.ai_lab_isolated && (
        <div data-testid="ai-lab-candidate-config-banner"
          className="flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 mb-5 text-sm text-blue-900">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div><b>Kandidat nicht konfiguriert.</b> Die Bild-Kandidatenanfragen (Tattoo entfernen / Bild bearbeiten)
            sind deaktiviert, bis <code className="mx-1 px-1 bg-white rounded">RUNPOD_AI_LAB_API_KEY</code> und
            <code className="mx-1 px-1 bg-white rounded">RUNPOD_AI_LAB_IMAGE_ENDPOINT</code> als Test-/Vorschau-Wert
            gesetzt sind. Es wird bewusst <b>nicht</b> auf den produktiven RunPod-Endpoint zurückgegriffen
            {cfg.ai_lab_key_set !== cfg.ai_lab_endpoint_set && (
              <> (aktuell fehlt {cfg.ai_lab_key_set ? "der Endpoint" : cfg.ai_lab_endpoint_set ? "der API-Key" : "Key und Endpoint"})</>
            )}.</div>
        </div>
      )}

      {/* Avatar-Bereich */}
      <div className="rounded-lg border border-[#EEE] p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Users size={16} className="text-[#0A0A0A]" />
          <h3 className="text-sm font-semibold text-[#333]">Virtueller Avatar (Referenz-Bilder)</h3>
        </div>
        <p className="text-[11px] text-[#888] mb-3">
          Mehrere Bilder derselben Person hochladen → Avatar anlegen. Bei „Bild bearbeiten" / „Bild → Video"
          kannst Du den Avatar als Referenz nutzen, damit dieselbe Person generiert wird.
          (Referenz-Modus; ein späteres LoRA-Feintraining bauen wir auf, sobald das grundlegend läuft.)
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <input data-testid="ai-lab-avatar-name" value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Avatar-Name (z. B. Testperson A)"
              className="w-full rounded-lg border border-[#DDD] p-2 text-sm focus:border-[#0A0A0A] outline-none" />
            <label data-testid="ai-lab-avatar-files"
              className="flex items-center justify-center gap-2 border-2 border-dashed border-[#DDD] rounded-lg p-3 cursor-pointer hover:border-[#0A0A0A] text-xs text-[#666]">
              <Upload size={16} /> {newFiles.length ? `${newFiles.length} Bild(er) gewählt` : "Mehrere Bilder wählen (max 8)"}
              <input type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => setNewFiles(Array.from(e.target.files || []).slice(0, 8))} />
            </label>
            <button data-testid="ai-lab-avatar-create" onClick={createAvatar} disabled={creatingAvatar}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#0A0A0A] text-[#0A0A0A] py-2 text-xs font-semibold disabled:opacity-40 hover:bg-[#0A0A0A] hover:text-white transition-colors">
              {creatingAvatar ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Avatar anlegen
            </button>
          </div>

          <div className="space-y-1">
            {avatars.length === 0 && <p className="text-xs text-[#AAA]">Noch keine Avatare.</p>}
            {avatars.map((a) => (
              <div key={a.avatar_id} data-testid={`ai-lab-avatar-${a.avatar_id}`}
                className={`flex items-center justify-between rounded border px-2 py-1.5 text-xs ${selectedAvatar === a.avatar_id ? "border-[#0A0A0A] bg-[#F5F5F5]" : "border-[#EEE]"}`}>
                <button onClick={() => setSelectedAvatar(selectedAvatar === a.avatar_id ? null : a.avatar_id)}
                  className="flex items-center gap-2 flex-1 text-left" data-testid={`ai-lab-avatar-select-${a.avatar_id}`}>
                  {selectedAvatar === a.avatar_id ? <Check size={14} className="text-emerald-600" /> : <span className="w-3.5" />}
                  <img src={`${BASE}/api/admin/ai-lab/avatars/${a.avatar_id}/image/0`} alt="" className="w-7 h-7 rounded object-cover border border-[#DDD]" />
                  <span className="text-[#333] font-medium">{a.name}</span>
                  <span className="text-[#999]">· {a.image_count} Bild(er)</span>
                </button>
                <button onClick={() => deleteAvatar(a.avatar_id)} data-testid={`ai-lab-avatar-delete-${a.avatar_id}`}
                  className="text-[#BBB] hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Generierung */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#333] mb-2">
              1 · {task === "tattoo_removal" ? "Foto" : "Foto (optional, wenn Avatar gewählt)"}
            </label>
            <label data-testid="ai-lab-upload"
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#DDD] rounded-lg p-6 cursor-pointer hover:border-[#0A0A0A] transition-colors">
              {uploading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} className="text-[#999]" />}
              <span className="text-xs text-[#666]">{src ? "Anderes Bild wählen" : "Bild hochladen (jpg/png/webp)"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="ai-lab-file-input" />
            </label>
            {src && <img src={src.previewUrl} alt="Quelle" className="mt-3 rounded-lg max-h-56 object-contain border border-[#EEE]" />}
            {selectedAvatar && <p className="text-[11px] text-emerald-700 mt-2">Avatar aktiv: {avatars.find((a) => a.avatar_id === selectedAvatar)?.name}</p>}

            {task === "image_edit" && (
              <div className="mt-4" data-testid="ai-lab-upload2-wrap">
                <label className="block text-xs font-semibold text-[#333] mb-2">
                  2. Foto (optional) – Zielumgebung / Szene
                </label>
                <p className="text-[11px] text-[#888] mb-2">
                  z. B. Person aus Foto 1 in die Umgebung von Foto 2 setzen. Beschreibe im Prompt, was passieren soll.
                </p>
                <label data-testid="ai-lab-upload2"
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#DDD] rounded-lg p-5 cursor-pointer hover:border-[#0A0A0A] transition-colors">
                  {uploading2 ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} className="text-[#999]" />}
                  <span className="text-xs text-[#666]">{src2 ? "Anderes 2. Bild wählen" : "2. Bild hochladen (jpg/png/webp)"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onFile2} data-testid="ai-lab-file-input-2" />
                </label>
                {src2 && (
                  <div className="mt-3 flex items-start gap-2">
                    <img src={src2.previewUrl} alt="2. Bild" className="rounded-lg max-h-40 object-contain border border-[#EEE]" />
                    <button type="button" data-testid="ai-lab-upload2-remove" onClick={() => setSrc2(null)}
                      className="text-[11px] text-red-600 hover:underline">entfernen</button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#333] mb-2">2 · Aufgabe</label>
            <div className="grid grid-cols-3 gap-2">
              {TASKS.map((t) => (
                <button key={t.key} data-testid={`ai-lab-task-${t.key}`} onClick={() => setTask(t.key)}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-[11px] transition-colors ${task === t.key ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#DDD] text-[#666] hover:border-[#999]"}`}>
                  <t.icon size={16} /> {t.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#888] mt-1">{TASKS.find((t) => t.key === task)?.hint}</p>
            {task === "image_to_video" && cfg && !cfg.ai_lab_video_ready && (
              <div data-testid="ai-lab-video-unavailable" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2 mt-2 text-[11px] text-amber-900">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span><b>Bild → Video ist noch nicht aktiviert.</b> Derselbe KI-Labor-Worker kann Bild und Video – sobald das Worker-Image Wan 2.2 enthält und neu deployt ist. Danach einmalig <code>RUNPOD_AI_LAB_VIDEO_ENABLED=true</code> (Vorschau) setzen, dann läuft Video über denselben Endpoint.</span>
              </div>
            )}
          </div>

          {task !== "image_to_video" && (
            <div>
              <label className="block text-xs font-semibold text-[#333] mb-2">GPU-Endpunkt (A/B-Vergleich)</label>
              <div className="grid grid-cols-2 gap-2" data-testid="ai-lab-lane-switch">
                {["a", "b"].map((ln) => {
                  const info = cfg?.ai_lab_lanes?.[ln];
                  const configured = !!info?.configured;
                  const label = info?.label || (ln === "a" ? "2511 · MIG 24 GB" : "2509 · 96 GB");
                  const active = lane === ln;
                  return (
                    <button key={ln} data-testid={`ai-lab-lane-${ln}`}
                      onClick={() => { if (configured) { setLane(ln); loadHealth(ln); } }}
                      disabled={!configured}
                      className={`flex flex-col items-start gap-0.5 rounded-lg border p-2 text-left transition-colors ${active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : configured ? "border-[#DDD] text-[#444] hover:border-[#999]" : "border-[#EEE] text-[#BBB] cursor-not-allowed"}`}>
                      <span className="text-[11px] font-semibold">{ln === "a" ? "A · Kandidat" : "B · Vergleich"}</span>
                      <span className="text-[11px]">{label}{!configured && " · nicht konfiguriert"}</span>
                    </button>
                  );
                })}
              </div>
              {!cfg?.ai_lab_lanes?.b?.configured && (
                <p className="text-[11px] text-[#999] mt-1">
                  Endpunkt B (2509) wird aktiv, sobald <code className="px-1 bg-[#F3F3F3] rounded">RUNPOD_AI_LAB_IMAGE_ENDPOINT_B</code>
                  {" "}(optional <code className="px-1 bg-[#F3F3F3] rounded">RUNPOD_AI_LAB_API_KEY_B</code>) als Test-/Vorschau-Wert gesetzt ist.
                </p>
              )}
            </div>
          )}

          {task !== "tattoo_removal" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-[#333]">3 · Prompt</label>
                <button data-testid="ai-lab-enhance" onClick={enhance} disabled={enhancing}
                  className="flex items-center gap-1 text-[11px] text-[#0A0A0A] font-semibold hover:underline disabled:opacity-40">
                  {enhancing ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />} Mit KI optimieren
                </button>
              </div>
              <textarea data-testid="ai-lab-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                rows={4} placeholder={isVideo ? "Kurze Idee, z. B. langsame Kamerafahrt am Fenster" : "Kurze Idee, z. B. Hintergrund durch Schlafzimmer ersetzen"}
                className="w-full rounded-lg border border-[#DDD] p-2 text-sm focus:border-[#0A0A0A] outline-none" />
              <p className="text-[11px] text-[#999] mt-1">Tipp: kurze Idee eingeben, dann „Mit KI optimieren" – das System baut daraus ein optimiertes Generierungs-Prompt (danach frei editierbar).</p>
            </div>
          )}

          {!isVideo && (
            <div>
              <label className="text-xs font-semibold text-[#333] mb-2 block">
                Ausschließen (Negativ-Prompt) <span className="font-normal text-[#999]">– optional</span>
              </label>
              <textarea data-testid="ai-lab-negative-prompt" value={negativePrompt} onChange={(e) => setNegativePrompt(e.target.value)}
                rows={2} placeholder="Was NICHT erscheinen soll, z. B. Brille, Hintergrundwechsel, unscharf, Text"
                className="w-full rounded-lg border border-[#DDD] p-2 text-sm focus:border-[#0A0A0A] outline-none" />
              <p className="text-[11px] text-[#999] mt-1">Wirkt nur bei Edit-Stärke &gt; 1 (unten). „Mit KI optimieren" füllt dieses Feld automatisch passend.</p>
            </div>
          )}

          {!isVideo && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-[#333]">Edit-Stärke (true_cfg_scale)</label>
                <span data-testid="ai-lab-cfg-value" className="text-xs font-mono text-[#0A0A0A]">{cfgScale.toFixed(1)}</span>
              </div>
              <input data-testid="ai-lab-cfg" type="range" min="1" max="7" step="0.5"
                value={cfgScale} onChange={(e) => setCfgScale(parseFloat(e.target.value))}
                className="w-full accent-[#0A0A0A]" />
              <p className="text-[11px] text-[#999] mt-1">Niedrig (1–2) = schwache Änderung / Bild bleibt fast gleich. Empfohlen 4. Höher (5–7) = stärkere, aber riskantere Edits.</p>
            </div>
          )}

          {!isVideo && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-[#333]">Schritte (steps)</label>
                <span data-testid="ai-lab-steps-value" className="text-xs font-mono text-[#0A0A0A]">{steps}</span>
              </div>
              <input data-testid="ai-lab-steps" type="range" min="10" max="50" step="5"
                value={steps} onChange={(e) => setSteps(parseInt(e.target.value, 10))}
                className="w-full accent-[#0A0A0A]" />
              <p className="text-[11px] text-[#999] mt-1">Mehr Schritte = feineres Ergebnis, aber linear mehr Zeit. 30 ist meist genug; 40 = Standard. Von 40 → 30 spart ~25 % Zeit.</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-[#333]">Seed (Reproduzierbarkeit)</label>
              <button data-testid="ai-lab-seed-random" type="button" onClick={() => setSeed(Math.floor(Math.random() * 1000000))}
                className="text-[11px] text-[#0A0A0A] font-semibold hover:underline">Zufällig</button>
            </div>
            <input data-testid="ai-lab-seed" type="number" value={seed}
              onChange={(e) => setSeed(parseInt(e.target.value || "0", 10))}
              className="w-full rounded-lg border border-[#DDD] p-2 text-sm focus:border-[#0A0A0A] outline-none" />
            <p className="text-[11px] text-[#999] mt-1">Fester Seed (z. B. 0) = gleiches Bild/Prompt liefert dasselbe Ergebnis. Zum Variieren den Seed ändern.</p>
          </div>

          <button data-testid="ai-lab-submit" onClick={submit} disabled={busy || uploading || (task !== "image_to_video" && !cfg?.ai_lab_lanes?.[lane]?.configured) || (task === "image_to_video" && cfg && !cfg.ai_lab_video_ready)}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#0A0A0A] text-white py-2.5 text-sm font-semibold disabled:opacity-40 hover:bg-[#222] transition-colors">
            {busy ? <><Loader2 className="animate-spin" size={16} /> Wird generiert …</> : <><Play size={16} /> Generieren</>}
          </button>

          {err && <p data-testid="ai-lab-error" className="text-xs text-red-600">{err}</p>}
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#333] mb-2">Ergebnis</label>
          <div data-testid="ai-lab-result" className="rounded-lg border border-[#EEE] bg-[#FAFAFA] min-h-[220px] flex items-center justify-center p-3">
            {busy && (
              <div className="flex flex-col items-center gap-2 text-center max-w-xs">
                <Loader2 className="animate-spin text-[#888]" size={22} />
                <span className="text-xs font-semibold text-[#555]" data-testid="ai-lab-job-status">
                  {job?.status || "…"}{waitSec > 0 && <span className="text-[#AAA] font-normal"> · {waitSec}s</span>}
                  {job?.endpoint_label && <span className="text-[#AAA] font-normal"> · {job.endpoint_label}</span>}
                </span>
                {queueStuck && (
                  <div data-testid="ai-lab-queue-hint" className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900">
                    <b>Job wartet in der RunPod-Warteschlange.</b> Es ist gerade keine GPU frei
                    {health?.state === "throttled" ? " (throttled)" : ""}. Der Job startet automatisch, sobald
                    eine GPU verfügbar ist. Du kannst warten oder abbrechen.
                  </div>
                )}
                <button data-testid="ai-lab-cancel" onClick={cancelJob}
                  className="mt-1 inline-flex items-center gap-1 rounded-lg border border-[#DDD] px-3 py-1 text-[11px] font-semibold text-[#666] hover:border-red-400 hover:text-red-600 transition-colors">
                  Abbrechen
                </button>
              </div>
            )}
            {!busy && resultUrl && !isVideo && <img data-testid="ai-lab-result-image" src={resultUrl} alt="Ergebnis" className="rounded-lg max-h-[420px] object-contain" />}
            {!busy && resultUrl && isVideo && <video data-testid="ai-lab-result-video" src={resultUrl} controls className="rounded-lg max-h-[420px]" />}
            {!busy && !resultUrl && <span className="text-xs text-[#AAA]">Noch kein Ergebnis</span>}
          </div>

          {report && (
            <div data-testid="ai-lab-report" className="mt-3 rounded-lg border border-[#EEE] bg-white p-3 text-[11px]">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-[#333] text-xs">Technischer Bericht</span>
                <span className="text-[#AAA]">· nur Admin/Owner · keine Bilddaten</span>
              </div>

              {/* Parameter */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-2">
                <span className="text-[#888]">Seed</span><span className="font-mono text-[#333]" data-testid="report-seed">{report.params?.seed ?? "–"}</span>
                <span className="text-[#888]">Steps</span><span className="font-mono text-[#333]">{report.params?.steps ?? "–"}</span>
                <span className="text-[#888]">true_cfg_scale</span><span className="font-mono text-[#333]">{report.params?.true_cfg_scale ?? "–"}</span>
                <span className="text-[#888]">Quell-Maße</span><span className="font-mono text-[#333]">{report.params?.source_dims ? report.params.source_dims.join("×") : "–"}</span>
                <span className="text-[#888]">RunPod delay (ms)</span><span className="font-mono text-[#333]" data-testid="report-delay">{report.runpod_timing_ms?.delay ?? "–"}</span>
                <span className="text-[#888]">RunPod execution (ms)</span><span className="font-mono text-[#333]" data-testid="report-exec">{report.runpod_timing_ms?.execution ?? "–"}</span>
              </div>

              {/* Worker-Metriken */}
              {report.worker_metrics && Object.keys(report.worker_metrics).length > 0 ? (
                <div className="border-t border-[#F0F0F0] pt-2 mb-2">
                  <div className="text-[#888] mb-1">Worker-Messwerte</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {Object.entries(report.worker_metrics).map(([k, v]) => (
                      <div key={k} className="contents">
                        <span className="text-[#888]">{k}</span>
                        <span className="font-mono text-[#333] truncate">{Array.isArray(v) ? v.join("×") : String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[#999] border-t border-[#F0F0F0] pt-2 mb-2">
                  Dieses Deployment liefert noch keine Worker-Messwerte (GPU/VRAM/Ladezeit). Erst nach dem
                  Deploy des instrumentierten 2511-Builds.
                </p>
              )}

              {/* Fehlende Metriken */}
              {report.missing_metrics?.length > 0 && (
                <div className="border-t border-[#F0F0F0] pt-2">
                  <div className="text-[#888] mb-1">Vom Deployment NICHT geliefert</div>
                  <div className="flex flex-wrap gap-1" data-testid="report-missing">
                    {report.missing_metrics.map((m) => (
                      <span key={m} className="rounded bg-amber-50 border border-amber-200 text-amber-800 px-1.5 py-0.5 font-mono">{m}</span>
                    ))}
                  </div>
                </div>
              )}

              {report.error && (
                <p className="border-t border-[#F0F0F0] pt-2 text-red-600">
                  Fehler: {report.error}{report.error_phase ? ` · Phase: ${report.error_phase}` : ""}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[#333]">Letzte Läufe</h3>
          <button data-testid="ai-lab-refresh" onClick={loadRecent} className="text-[#888] hover:text-[#0A0A0A]"><RefreshCw size={14} /></button>
        </div>
        <div className="space-y-1">
          {recent.length === 0 && <p className="text-xs text-[#AAA]">Noch keine Läufe.</p>}
          {recent.map((j) => (
            <div key={j.job_id} data-testid={`ai-lab-job-${j.job_id}`}
              className="flex items-center justify-between rounded border border-[#EEE] px-3 py-1.5 text-xs">
              <span className="text-[#666]">{TASKS.find((t) => t.key === j.task)?.label || j.task} · {j.kind}{j.endpoint_label ? ` · ${j.endpoint_label}` : ""}</span>
              <div className="flex items-center gap-3">
                <button data-testid={`ai-lab-report-btn-${j.job_id}`} onClick={() => fetchReport(j.job_id)}
                  className="text-[#888] hover:text-[#0A0A0A] hover:underline">Bericht</button>
                <span className={`font-semibold ${j.status === "COMPLETED" ? "text-emerald-600" : j.status === "FAILED" ? "text-red-600" : "text-[#999]"}`}>{j.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
